// React component to display the status update input

import React from 'react';
import TwSelect, { TwSelectOption } from '../../../TwSelects/TwSelects';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import { logRocketTrack } from '../../../../utils/logRocketUtils';
import useAuth from '../../../../providers/Auth/Auth.provider';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { nanoid } from '@reduxjs/toolkit';
import { setLoadCalendarReRenderId } from '../../../../redux/features/orders/ordersSlice';
import { StopStatus, TRIP_STATUS } from '../../../../redux/api/trips/trips.model';
import useStopStatusProgression from '../../../../hooks/useStopStatusProgression';
import { setTripDetailReload } from '../../../../redux/features/navigation/navigationSlice';
import { useLocation } from 'react-router-dom';
import RTKUtils from '../../../../utils/rtkUtils';

export interface StopStatusUpdateInputProps {
  /**
   * status of the stop
   * */
  status: StopStatus;

  /**
   * stop id of the stop
   * */
  stopId?: string | null;

  /**
   * trip status for RTK invalidation
   * */
  tripStatus: TRIP_STATUS;
  /**
   *
   * Trip id for RTK invalidation
   */
  tripId: string;

  reloadData?: () => void;
}

const options: TwSelectOption[] = [
  { label: 'Not started', value: StopStatus.notStarted },
  { label: 'In Transit', value: StopStatus.inTransit },
  { label: 'Arrived', value: StopStatus.arrived },
  { label: 'Complete', value: StopStatus.complete }
];

const StopStatusUpdateInput: React.FC<StopStatusUpdateInputProps> = ({
  status,
  stopId,
  tripStatus,
  tripId,
  reloadData
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();
  const route = useLocation();
  const isCalendarOpen = useAppSelector((state) => state.navigation.isCalendarOpen);
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const { updateStopStatus } = useStopStatusProgression();

  const handleChange = (e: any): void => {
    const newStatus = e.target.value as StopStatus;

    if (!stopId) {
      logRocketTrack('Status update', {
        action: 'Change stop status',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Stop not found, please try again',
        variant: 'error'
      });
    } else {
      const tagsToInvalidate = RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);
      updateStopStatus(stopId, newStatus, tripStatus, tagsToInvalidate)
        .then((res) => {
          if (isCalendarOpen) {
            const reRenderId = nanoid();
            dispatch(setLoadCalendarReRenderId(reRenderId));
          }
          if (isLoadDetailOpen) {
            dispatch(setTripDetailReload(true));
          }
          reloadData && reloadData();
        })
        .catch((err) => {
          logRocketTrack('Status update', {
            action: 'Change stop status',
            severity: 'high',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: 'Failed to update stop status, please try again',
            variant: 'error'
          });
        });
    }
  };

  return (
    <div style={{ minWidth: '150px' }}>
      <TwSelect
        id="stopStatusUpdateInput"
        name="stopStatusUpdateInput"
        value={status}
        optionList={options}
        onInputChange={handleChange}
        error={false}
        selectSx={{
          '& .MuiInputBase-input': {
            backgroundColor: 'transparent !important',
            color: '#3A4DE9 !important',
            paddingLeft: '0px !important'
          },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
        }}
      />
    </div>
  );
};

export default StopStatusUpdateInput;
